const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';
import { getButtonClasses } from '../../../config/colors';


// Utility to derive a friendly class from the color value & label
const colorToClass = (value, label) => {
  if (!value) return '';
  const varMatch = /^var\(--([^)]+)\)/.exec(value);
  if (varMatch) return `color-var-${varMatch[1].toLowerCase()}`;
  if (value.startsWith('#')) return `color-${value.slice(1).toLowerCase()}`;
  if (label) return `color-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return '';
};

const EmblaCarousel = ({ data, id }) => {
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.search?.subrequests?.[id]);
  const allSubrequests = useSelector((state) => state.search?.subrequests || {});

  // Resolved path map: uid -> '/site/path'
  const [resolvedPaths, setResolvedPaths] = useState({});

  // When a resolveuid subrequest lands, extract the path from its first result
  useEffect(() => {
    if (!data.useListing || !data.query?.query) return;
    const uidCriteria = data.query.query.filter(
      (c) => c.i === 'path' && c.o === 'plone.app.querystring.operation.string.absolutePath',
    );
    uidCriteria.forEach((c) => {
      if (typeof c.v !== 'string') return;
      if (c.v.startsWith('/')) return; // plain path — no UID to resolve
      const uid = c.v.split('::')[0];
      if (resolvedPaths[uid]) return;
      const resolveId = `${id}-resolveuid-${uid}`;
      const result = allSubrequests[resolveId];
      if (result?.items?.length > 0) {
        const path = flattenToAppURL(result.items[0]['@id']);
        setResolvedPaths((prev) => ({ ...prev, [uid]: path }));
      }
    });
  }, [allSubrequests, data.useListing, data.query, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data.useListing || !data.query?.query) return;
    const uidCriteria = data.query.query.filter(
      (c) => c.i === 'path' && c.o === 'plone.app.querystring.operation.string.absolutePath',
    );
    if (!uidCriteria.length) return;

    // Parse "uid::depth" — resolve each unique UID we haven't seen yet.
    // Older Volto versions may store a plain path (e.g. "/Plone/site/folder")
    // instead of a "uid::depth" string — skip those, they don't need resolving.
    const toResolve = uidCriteria
      .map((c) => {
        if (typeof c.v !== 'string') return null;
        if (c.v.startsWith('/')) return null; // already a plain path — no UID lookup needed
        return c.v.split('::')[0];
      })
      .filter(Boolean)
      .filter((uid) => !resolvedPaths[uid]);

    if (!toResolve.length) return;

    toResolve.forEach((uid) => {
      const resolveId = `${id}-resolveuid-${uid}`;
      dispatch(
        searchContent('', {
          UID: uid,
          metadata_fields: [],
          b_size: 1,
        }, resolveId),
      );
    });
  }, [data.useListing, data.query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the best available image URL from a catalog brain.
  // This site has widespread catalog corruption where image object fields
  // (image_scales, preview_image, lead_image) contain raw NamedBlobImage
  // objects that plone.restapi cannot serialise. We therefore rely entirely
  // on @@images traversal using the safe string field names only.
  const getImageUrl = (item) => {
    if (item.image_field && item['@id']) {
      return `${item['@id']}/@@images/${item.image_field}`;
    }
    if (item.preview_image_field && item['@id']) {
      return `${item['@id']}/@@images/${item.preview_image_field}`;
    }
    return '';
  };

  const getListingImageUrl = (item) => {
    if (item['@type'] === 'Image') return `${item['@id']}/@@images/image`;
    return getImageUrl(item);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch (e) { return dateString; }
  };

  const renderDate = (slide) => {
    const mode = data.dateDisplay;
    if (!mode || mode === 'none') return null;
    if (mode === 'effective') {
      if (!slide.effectiveDate) return null;
      return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
    }
    if (mode === 'start') {
      if (slide.startDate) {
        return <p className="slide-date">Starts {formatDate(slide.startDate)}</p>;
      }
      if (slide.effectiveDate) {
        return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
      }
      return null;
    }
    return null;
  };

  const convertItemToSlide = (item) => ({
    heading: item.title || '',
    content: item.description || '',
    image: null,
    link: item,
    buttonText: data.listingButtonText || 'Read more',
    effectiveDate: item.effective || null,
    startDate: item.start || null,
    subjects: item.Subject || [],
    '@id': item['@id'] || null,
    image_field: item.image_field || null,
    image_scales: item.image_scales || null,
    preview_image: item.preview_image || null,
    preview_image_scales: item.preview_image_scales || null,
    preview_image_field: item.preview_image_field || null,
    lead_image: item.lead_image || null,
    isFromListing: true,
  });

  const getSlides = () => {
    let slides = [];
    if (data.useListing && searchResults?.items && searchResults.items.length > 0) {
      // Derive the configured path so we can exclude the container folder itself.
      // Plone's path index includes the folder at depth 0, so without this the
      // folder page appears alongside its children.
      const configuredPath = (() => {
        const criterion = data.query?.query?.find(
          (c) => c.i === 'path' && c.o === 'plone.app.querystring.operation.string.absolutePath',
        );
        if (!criterion || typeof criterion.v !== 'string') return null;
        if (criterion.v.startsWith('/')) return criterion.v.split('::')[0];
        const uid = criterion.v.split('::')[0];
        return resolvedPaths[uid] || null;
      })();
      slides = searchResults.items
        .filter((item) => {
          if (!configuredPath) return true;
          const itemPath = flattenToAppURL(item['@id'] || '');
          return itemPath !== configuredPath;
        })
        .map(convertItemToSlide);
    }
    if (!data.useListing || (data.useListing && data.appendManualSlides)) {
      const manualSlides = (data.slides || []).map(slide => ({
        ...slide,
        isFromListing: false
      }));
      slides = slides.concat(manualSlides);
    }
    return slides;
  };

  // ---- Tag filter state ----
  const parsedFilterTags = React.useMemo(() => {
    if (!data.filterTags) return [];
    return data.filterTags.split(',').map((t) => t.trim()).filter(Boolean);
  }, [data.filterTags]);

  const [activeTag, setActiveTag] = useState('all');

  // Reset to 'all' if tags change
  useEffect(() => {
    setActiveTag('all');
  }, [data.filterTags]);

  const allSlides = getSlides();

  // Determine which tags actually have matching slides
  const tagsWithSlides = React.useMemo(() => {
    return parsedFilterTags.filter((tag) =>
      allSlides.some((slide) => {
        const subjects = slide.subjects || slide.link?.Subject || [];
        return Array.isArray(subjects)
          ? subjects.includes(tag)
          : subjects === tag;
      }),
    );
  }, [parsedFilterTags, allSlides]);

  const slides = React.useMemo(() => {
    if (!parsedFilterTags.length || activeTag === 'all') return allSlides;
    return allSlides.filter((slide) => {
      const subjects = slide.subjects || slide.link?.Subject || [];
      return Array.isArray(subjects)
        ? subjects.includes(activeTag)
        : subjects === activeTag;
    });
  }, [allSlides, activeTag, parsedFilterTags]);

  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data.minSlidesOnMobile, 10) || 1;

  const calculateSlidesToShow = React.useCallback(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    if (w <= 540) return Math.max(1, minSlidesOnMobile);
    if (w <= 768) return Math.min(slidesToShow, Math.max(1, minSlidesOnMobile));
    return slidesToShow;
  }, [slidesToShow, minSlidesOnMobile]);

  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(calculateSlidesToShow);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      setEffectiveSlidesToShow(calculateSlidesToShow());
      setIsMobile(window.innerWidth <= 768);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [calculateSlidesToShow]);

  // Peek disabled on mobile and when fewer than 5 slides are visible
  const peekActive = data.centeredPeek && !isMobile && slides.length >= 5;

  useEffect(() => {
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: [
          'title', 
          'description', 
          'image_field',
          'preview_image_field',
          'effective',
          'start',
          'remoteUrl',
          'Subject',
        ],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          // Transform the Volto query widget's criteria array into flat searchContent params.
          // We do this generically so every criterion passes through regardless of index name.
          const transformedQuery = {};
          let pathPending = false;
          data.query.query.forEach(criterion => {
            if (!criterion.i) return;
            if (
              criterion.i === 'path' &&
              criterion.o === 'plone.app.querystring.operation.string.absolutePath' &&
              typeof criterion.v === 'string'
            ) {
              // Newer Volto stores "uid::depth"; older Volto stores a plain "/path"
              // or "/path::depth" (some older Volto builds). searchContent expects
              // path as a plain string; depth is a separate "path.depth" param.
              if (criterion.v.startsWith('/')) {
                // Plain path — may be "/path" or "/path::depth"
                const [plainPath, depthStr] = criterion.v.split('::');
                transformedQuery.path = plainPath;
                // Depth from the value string takes priority; fall back to criterion.depth
                const depth = depthStr !== undefined
                  ? parseInt(depthStr, 10)
                  : (criterion.depth !== undefined ? parseInt(criterion.depth, 10) : undefined);
                if (depth !== undefined && !isNaN(depth)) {
                  transformedQuery['path.depth'] = depth;
                }
              } else {
                const [uid, depthStr] = criterion.v.split('::');
                const resolvedPath = resolvedPaths[uid];
                if (!resolvedPath) {
                  pathPending = true;
                  return;
                }
                transformedQuery.path = resolvedPath;
                if (depthStr !== undefined) {
                  transformedQuery['path.depth'] = parseInt(depthStr, 10);
                }
              }
            } else {
              const keyMap = { portal_type: 'portal_type', Subject: 'Subject', review_state: 'review_state' };
              const key = keyMap[criterion.i] || criterion.i;
              transformedQuery[key] = criterion.v;
            }
          });
          if (pathPending) return; // don't dispatch until path is resolved
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order) transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit) transformedQuery.b_size = parseInt(data.query.limit, 10);
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          // Query is already a flat object (e.g. set programmatically)
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit) searchOptions.b_size = parseInt(data.query.limit, 10);
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = baseOptions;
      } else {
        searchOptions = { ...baseOptions, query: data.query };
      }

      dispatch(searchContent(searchTerm, searchOptions, id));
    }
  }, [data.useListing, data.query, resolvedPaths, dispatch, id]);

  const carouselType = data.carouselType || 'default';

  const configOverrides = {
    default: { align: 'start', loop: data.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };

  const presetConfig = getPresetConfig(data.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };

  const [selectedIndex, setSelectedIndex] = useState(0);

  const plugins = data.autoplay ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 4000, stopOnInteraction: false })] : [];
  const [viewportRef, embla] = useEmblaCarousel({
    ...config,
    loop: data.loop ?? true,
    align: peekActive ? 'center' : 'start',
    slidesToScroll: peekActive ? 1 : effectiveSlidesToShow,
    ...(peekActive ? { containScroll: false } : {}),
  }, plugins);

  

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    onSelect(); // set initial selected index
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      try { embla.off('select', onSelect); } catch (e) {}
      try { embla.off('reInit', onSelect); } catch (e) {}
    };
  }, [embla]);

  // Scroll back to start whenever active tag changes
  useEffect(() => {
    if (!embla) return;
    embla.reInit();
    embla.scrollTo(0, true);
    setSelectedIndex(0);
  }, [activeTag, embla]);
const getHref = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return '#';
    if (item['@type'] === 'Link' && item.remoteUrl) return item.remoteUrl;
    const url = item['@id'] || '';
    const isFile = item['@type'] === 'File';
    return url ? (isFile ? `${url}/@@download/file` : url) : '#';
  };

  const _displayMode = data.displayMode || data.mode || 'full';
  const isLogoMarquee = _displayMode === 'logo-marquee';
  const variantClass = `type-${_displayMode}`;
  const isImageOnly = _displayMode === 'image-only';
  const isImageTopMode = _displayMode === 'image-top';

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons || _displayMode === 'image-only') return null;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    const { className, style } = getButtonClasses(data.slideButtonStyle, 'embla__slide-btn');
    return (
      <a
        href={href}
        className={className}
        style={style}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;
    const href = getHref(slide.link);
    if (!href || href === '#') return;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  const imageFor = (slide) => {
    if (slide.isFromListing) {
      // Try slide-level fields first (populated by convertItemToSlide)
      // then fall back to the full item object on slide.link
      return getImageUrl(slide) || getListingImageUrl(slide.link);
    }
    const image = Array.isArray(slide.image) ? slide.image[0] : slide.image;
    return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
  };

  const heightClass = data.equalHeight ? 'equal-height' : '';
  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const colorClass = colorToClass(data.backgroundColor);
  const toneClass = data.textTone === 'light' ? 'tone-light' : 'tone-dark';
  const blockId = data.title
    ? data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    : undefined;

  const showArrows = data.hideArrows !== true && slides.length > 1 && !isLogoMarquee;
  const showDots = data.hideDots !== true && slides.length > 0 && !isLogoMarquee;

  const pageCount = peekActive
    ? slides.length
    : Math.ceil(slides.length / effectiveSlidesToShow);

  return (
    <div
      id={blockId}
      className={`embla ${heightClass} ${data.isFullWidth ? 'full-width' : ''} ${peekActive ? 'centered-peek' : ''} align-${data.alignment || 'left'} ${modeClass} ${variantClass} ${toneClass} ${colorClass} ${data.outerClassName || ''}`}
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      <div className="embla__inner">

        {/* Logo marquee */}
        {isLogoMarquee ? (
          <>
            {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
            <div
              className={`logo-marquee ${data.pauseOnHover ? 'pause-on-hover' : ''}`}
              style={{
                '--marquee-duration': `${parseInt(data.marqueeSpeed, 10) || 20}s`,
                '--logo-height': `${parseInt(data.logoHeight, 10) || 48}px`,
                '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
                '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
              }}
            >
              <div className="logo-marquee__track">
                {[...slides, ...slides].map((slide, i) => {
                  const src = imageFor(slide);
                  if (!src) return null;
                  const alt = slide.heading || 'Logo';
                  const href = getHref(slide.link);
                  const clickable = data.clickableSlides && href && href !== '#';
                  const img = <img className="logo-marquee__img" src={src} alt={alt} />;
                  return (
                    <div className="logo-marquee__item" key={`r-${i}`}>
                      {clickable ? (
                        <a
                          href={href}
                          className="logo-marquee__link"
                          target="_blank"
                        >
                          {img}
                        </a>
                      ) : img}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header row — title/description left, arrows+button right when arrowPosition=top */}
            {(data.title || data.description || (showArrows && data.arrowPosition === 'top')) && (
              <div className={`embla__header ${data.arrowPosition === 'top' ? 'embla__header--has-controls' : ''}`}>
                <div className="embla__header-top">
                  {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
                  {showArrows && data.arrowPosition === 'top' && slides.length > 1 && (() => {
                    const arrowStyleParsed = getButtonClasses(data.arrowStyle, 'embla__arrow');
                    const linkStyleParsed = getButtonClasses(data.headerLinkStyle, 'embla__header-link');
                    return (
                      <div className="embla__header-controls embla__header-controls--desktop">
                        <button
                          className={`embla__prev ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollPrev()}
                        >
                          <i className="bi bi-arrow-left-short"></i>
                        </button>
                        {data.headerLinkText && (
                          <a
                            href={getHref(data.headerLinkUrl)}
                            className={linkStyleParsed.className}
                            style={linkStyleParsed.style}
                            {...(getHref(data.headerLinkUrl).startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          >
                            {data.headerLinkText}
                          </a>
                        )}
                        <button
                          className={`embla__next ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollNext()}
                        >
                          <i className="bi bi-arrow-right-short"></i>
                        </button>
                      </div>
                    );
                  })()}
                </div>
                {data.description ? <p className="embla__description">{data.description}</p> : null}
                {showArrows && data.arrowPosition === 'top' && slides.length > 1 && (() => {
                  const arrowStyleParsed = getButtonClasses(data.arrowStyle, 'embla__arrow');
                  const linkStyleParsed = getButtonClasses(data.headerLinkStyle, 'embla__header-link');
                  return (
                    <div className="embla__header-controls embla__header-controls--mobile">
                      <button
                        className={`embla__prev ${arrowStyleParsed.className}`}
                        style={arrowStyleParsed.style}
                        onClick={() => embla && embla.scrollPrev()}
                      >
                        <i className="bi bi-arrow-left-short"></i>
                      </button>
                      {data.headerLinkText && (
                        <a
                          href={getHref(data.headerLinkUrl)}
                          className={linkStyleParsed.className}
                          style={linkStyleParsed.style}
                          {...(getHref(data.headerLinkUrl).startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {data.headerLinkText}
                        </a>
                      )}
                      <button
                        className={`embla__next ${arrowStyleParsed.className}`}
                        style={arrowStyleParsed.style}
                        onClick={() => embla && embla.scrollNext()}
                      >
                        <i className="bi bi-arrow-right-short"></i>
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Title/description standalone when no top controls */}
            {data.arrowPosition !== 'top' && !data.title && !data.description ? null : null}

            {/* Tag filter tabs */}
            {tagsWithSlides.length > 0 && (
              <div className="embla__filter-tabs" role="tablist" aria-label="Filter content">
                <button
                  role="tab"
                  aria-selected={activeTag === 'all'}
                  className={`embla__filter-tab ${activeTag === 'all' ? 'is-active' : ''}`}
                  onClick={() => setActiveTag('all')}
                >
                  All
                </button>
                {tagsWithSlides.map((tag) => (
                  <button
                    key={tag}
                    role="tab"
                    aria-selected={activeTag === tag}
                    className={`embla__filter-tab ${activeTag === tag ? 'is-active' : ''}`}
                    onClick={() => setActiveTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            <div className="embla__viewport" ref={viewportRef}>
              <div className="embla__container">
                {slides.map((slide, index) => {
                  const imageUrl = imageFor(slide);
                  const href = getHref(slide.link);
                  const isClickable = data.clickableSlides && href && href !== '#';

                  return (
                    <div className="embla__slide" key={index} style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}>
                      <div className="slide__content">
                      <div
                        className={`carousel-slide-inner ${isClickable ? 'clickable' : ''} ${isImageOnly ? 'image-only' : ''} ${isImageTopMode ? 'image-top' : ''} ${slide.isFromListing ? 'listing-slide' : 'manual-slide'} ${!isImageOnly && !isImageTopMode && !imageUrl ? 'no-image' : ''}`}
                        style={{ 
                          backgroundImage: !isImageOnly && !isImageTopMode && imageUrl ? `url(${imageUrl})` : 'none',
                          cursor: isClickable ? 'pointer' : 'default'
                        }}
                        onClick={() => handleSlideClick(slide)}
                      >
                        {isImageOnly ? (
                          imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={slide.heading || 'Slide image'} 
                            />
                          ) : (
                            <div className="image-placeholder">
                              No image
                            </div>
                          )
                        ) : isImageTopMode ? (
                          <div className="card card-image-top">
                            {imageUrl ? (
                              <div className="card-media">
                                <img src={imageUrl} alt={slide.heading || 'Slide image'} />
                              </div>
                            ) : null}
                            <div className="card-content">
                              {renderDate(slide)}
                              <h3>{slide.heading}</h3>
                              {!data.hideDescription && <p>{slide.content}</p>}
                              {renderLink(slide)}
                            </div>
                          </div>
                        ) : (
                          <div className="slide__body">
                            {renderDate(slide)}
                            <h3>{slide.heading}</h3>
                            {!data.hideDescription && <p>{slide.content}</p>}
                            {renderLink(slide)}
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {showArrows && slides.length > 1 && data.arrowPosition !== 'top' && (
              <div className="embla__controls" aria-hidden="false">
                <div className="embla__controls__buttons" aria-hidden="false">
                  <button className="embla__prev" onClick={() => embla && embla.scrollPrev()}><i className="bi bi-arrow-left-short"></i></button>
                  <button className="embla__next" onClick={() => embla && embla.scrollNext()}><i className="bi bi-arrow-right-short"></i></button>
                </div>
              </div>
            )}

            {showDots && pageCount > 1 && (
              <div className="embla__nav">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => embla && embla.scrollTo(i)}
                    className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmblaCarousel;